import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from './ui/button';
import {
  Calendar, MapPin, ChevronRight, Loader2, Sparkles,
  Filter, Image as ImageIcon, ArrowLeft,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
import { CardBanner } from './CardBanner';

const CATEGORIES = [
  { id: 'all', label: 'All' },
  { id: 'tournament', label: 'Tournaments' },
  { id: 'fitness', label: 'Fitness' },
  { id: 'screening', label: 'Screenings' },
  { id: 'multi-activity', label: 'Multi-Activity' },
];

const SPORTS = [
  { id: 'all', label: 'All sports' },
  { id: 'football', label: 'Football' },
  { id: 'padel', label: 'Padel' },
  { id: 'pickleball', label: 'Pickleball' },
  { id: 'badminton', label: 'Badminton' },
  { id: 'cricket', label: 'Cricket' },
];

const BADGE_TONE = {
  open: 'bg-emerald-100 text-emerald-700',
  filling_fast: 'bg-amber-100 text-amber-800',
  full: 'bg-red-100 text-red-700',
  ended: 'bg-gray-100 text-gray-600',
  cancelled: 'bg-gray-100 text-gray-500',
};
const BADGE_LABEL = {
  open: 'Open',
  filling_fast: 'Filling Fast',
  full: 'Full',
  ended: 'Ended',
  cancelled: 'Cancelled',
};

const fmtDateRange = (start, end) => {
  if (!start) return '';
  const s = new Date(start + 'T00:00:00');
  const sStr = s.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' });
  if (!end || end === start) return sStr;
  const e = new Date(end + 'T00:00:00');
  if (s.getMonth() === e.getMonth()) {
    return `${s.getDate()}–${e.getDate()} ${e.toLocaleDateString('en-IN', { month: 'short' })}`;
  }
  return `${sStr} – ${e.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}`;
};

const SPORT_EMOJI = {
  football: '\u26BD', padel: '\uD83C\uDFD3', pickleball: '\uD83C\uDFD3',
  badminton: '\uD83C\uDFF8', cricket: '\uD83C\uDFCF',
};

// ============== Listing page ==============

const EventsPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [venues, setVenues] = useState([]);
  const [category, setCategory] = useState('all');
  const [sport, setSport] = useState('all');
  const [venueId, setVenueId] = useState(searchParams.get('venue') || 'all');

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (category !== 'all') params.set('category', category);
      if (sport !== 'all') params.set('sport', sport);
      if (venueId !== 'all') params.set('venue_id', venueId);
      const res = await fetch(`${API_URL}/api/events/public/list?${params.toString()}`);
      const data = await res.json();
      setEvents(Array.isArray(data) ? data : []);
    } catch {
      setEvents([]);
    } finally {
      setLoading(false);
    }
  }, [category, sport, venueId]);

  useEffect(() => { load(); }, [load]);

  // Load venues for filter (best-effort — uses the public facilities list)
  useEffect(() => {
    fetch(`${API_URL}/api/facilities/public`)
      .then(r => r.ok ? r.json() : [])
      .then(d => setVenues(Array.isArray(d) ? d : []))
      .catch(() => {});
  }, []);

  return (
    <div className="min-h-screen bg-gray-50 pb-24">
      {/* Header */}
      <div className="bg-white border-b sticky top-0 z-10">
        <div className="max-w-5xl mx-auto px-4 py-3 flex items-center gap-3">
          <button onClick={() => navigate('/')} className="p-2 -ml-2 hover:bg-gray-100 rounded-lg" data-testid="events-back">
            <ArrowLeft className="h-5 w-5" />
          </button>
          <div className="min-w-0">
            <h1 className="text-lg font-bold leading-tight">Events &amp; Tournaments</h1>
            <p className="text-xs text-gray-500 -mt-0.5">Tournaments, fitness, screenings &amp; more</p>
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 pt-4">
        {/* Category chips — scroll on mobile */}
        <div className="flex items-center gap-2 overflow-x-auto pb-2 -mx-1 px-1 no-scrollbar" data-testid="events-category-filter">
          {CATEGORIES.map(c => (
            <button
              key={c.id}
              onClick={() => setCategory(c.id)}
              className={`flex-shrink-0 px-3 py-1.5 rounded-full text-xs font-semibold transition-colors ${
                category === c.id ? 'bg-gray-900 text-white' : 'bg-white border text-gray-600'
              }`}
              data-testid={`category-${c.id}`}
            >
              {c.label}
            </button>
          ))}
        </div>

        {/* Sport + Venue selects */}
        <div className="grid grid-cols-2 gap-2 mt-2">
          <div className="relative">
            <Filter className="h-3.5 w-3.5 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <select
              value={sport}
              onChange={e => setSport(e.target.value)}
              className="w-full pl-8 pr-3 py-2 text-xs rounded-full bg-white border appearance-none"
              data-testid="events-sport-filter"
            >
              {SPORTS.map(s => <option key={s.id} value={s.id}>{s.label}</option>)}
            </select>
          </div>
          <div className="relative">
            <MapPin className="h-3.5 w-3.5 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <select
              value={venueId}
              onChange={e => setVenueId(e.target.value)}
              className="w-full pl-8 pr-3 py-2 text-xs rounded-full bg-white border appearance-none"
              data-testid="events-venue-filter"
            >
              <option value="all">All venues</option>
              {venues.map(v => <option key={v.id} value={v.id}>{v.name}</option>)}
            </select>
          </div>
        </div>

        {/* Cards */}
        <div className="mt-5">
          {loading ? (
            <div className="flex items-center justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-gray-400" /></div>
          ) : events.length === 0 ? (
            <div className="bg-white rounded-2xl border p-10 text-center">
              <Sparkles className="h-10 w-10 text-gray-300 mx-auto mb-3" />
              <p className="text-sm text-gray-500">No events found</p>
              <p className="text-xs text-gray-400 mt-1">Try different filters or check back later.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="events-grid">
              {events.map(ev => <EventCard key={ev.id} event={ev} onClick={() => navigate(`/events/${ev.slug}`)} />)}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export const EventCard = ({ event, onClick }) => {
  const badge = event.registration_badge || 'open';
  const isFree = event.is_free;
  const priceLabel = isFree ? 'Free' : `From ₹${event.from_price || 0}`;

  return (
    <button
      onClick={onClick}
      className="text-left bg-white border rounded-2xl overflow-hidden shadow-sm hover:shadow-md transition-shadow flex flex-col"
      data-testid={`event-card-${event.slug}`}
    >
      {/* Banner */}
      <div className="aspect-video bg-gray-100 relative">
        <CardBanner
          src={event.banner_image_url ? `${API_URL}${event.banner_image_url}` : null}
          Icon={ImageIcon}
          testId={`event-banner-${event.slug}`}
        />
        {/* Status badge */}
        <span className={`absolute top-2 left-2 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wide ${BADGE_TONE[badge] || BADGE_TONE.open}`} data-testid={`badge-${event.slug}`}>
          {BADGE_LABEL[badge] || badge}
        </span>
        {event.is_multi_activity && (
          <span className="absolute top-2 right-2 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-indigo-600 text-white uppercase tracking-wide">
            Multi
          </span>
        )}
      </div>

      {/* Content */}
      <div className="p-4 flex flex-col gap-2 flex-1">
        <h3 className="font-bold text-base text-gray-900 line-clamp-2 leading-tight">{event.title}</h3>
        <p className="text-xs text-gray-500 flex items-center gap-1">
          <MapPin className="h-3 w-3" />{event.venue_name}
        </p>
        <p className="text-xs text-gray-500 flex items-center gap-1">
          <Calendar className="h-3 w-3" />{fmtDateRange(event.start_date, event.end_date)}
        </p>
        {event.sports?.length > 0 && (
          <div className="flex items-center gap-1.5 mt-0.5">
            {event.sports.map(s => (
              <span key={s} title={s} className="text-base">{SPORT_EMOJI[s] || ''}</span>
            ))}
          </div>
        )}
        <div className="flex items-center justify-between mt-auto pt-2">
          <span className={`font-bold text-sm ${isFree ? 'text-emerald-600' : ''}`}>{priceLabel}</span>
          <span className="flex items-center gap-1 text-xs font-semibold text-gray-900 bg-gray-100 px-3 py-1.5 rounded-full">
            View <ChevronRight className="h-3 w-3" />
          </span>
        </div>
      </div>
    </button>
  );
};

export default EventsPage;
